/**
 * SmartImportPage — top-level route for /import.
 *
 * Agent-style conversational flow: drop-zone → column confirm → event pick
 * → plan pick → name + submit → success. The reducer in state.ts owns the
 * phase machine; this file is plumbing + card rendering.
 *
 * Parent: see FORK-butler-buddy-smart-import-portal-2026-04-17.md.
 */

import { useEffect, useReducer, useState } from 'react'
import { AlertTriangle } from 'lucide-react'

import { useImportJob } from '../../hooks/useImportJob'
import { AgentBubble } from './components/AgentBubble'
import { ColumnConfirmCard } from './components/ColumnConfirmCard'
import { DropZone } from './components/DropZone'
import { EventPickerCard } from './components/EventPickerCard'
import { MmConnectCard, MmDisconnectRow } from './components/MmConnectCard'
import { NameAndSubmitCard } from './components/NameAndSubmitCard'
import { PlanPickerCard } from './components/PlanPickerCard'
import { PreviewCard } from './components/PreviewCard'
import { SuccessCard } from './components/SuccessCard'
import {
  defaultBatchName,
  initialState,
  reducer,
} from './state'
import type { MmSession } from './api'

export function SmartImportPage() {
  const [state, dispatch] = useReducer(reducer, initialState)
  const job = useImportJob()
  const [session, setSession] = useState<MmSession | null>(null)
  const [sessionError, setSessionError] = useState<string | null>(null)

  // Probe the MM session on mount. The backend returns {connected:false} when
  // the user hasn't run the bookmarklet yet — we stay in 'idle' then render
  // the MmConnectCard until they do.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const s = await job.getSession()
        if (cancelled) return
        setSession(s)
        dispatch({ type: 'mm-session-known', connected: s.connected })
      } catch (err) {
        if (cancelled) return
        setSessionError(
          err instanceof Error ? err.message : 'Failed to check MM session',
        )
      }
    })()
    return () => {
      cancelled = true
    }
  }, [job])

  async function handleManualConnect(auth: string, sessionCookie: string, alb?: string) {
    try {
      await job.connect({ auth, session: sessionCookie, alb })
      setSession({ connected: true, connectedAt: Date.now() })
      dispatch({ type: 'mm-connect-ok' })
    } catch {
      // error already surfaced via job.lastError
    }
  }

  async function handleDisconnect() {
    try {
      await job.disconnect()
    } finally {
      setSession({ connected: false })
      dispatch({ type: 'mm-disconnect' })
    }
  }

  async function handleFile(file: File) {
    dispatch({ type: 'file-selected' })
    try {
      const upload = await job.upload(file)
      dispatch({ type: 'upload-ok', upload })
    } catch (err) {
      dispatch({
        type: 'upload-failed',
        reason: err instanceof Error ? err.message : 'Upload failed',
      })
    }
  }

  async function handleColumnPick(columnIndex: number) {
    if (state.current.phase !== 'column-confirm') return
    const upload = state.current.upload
    try {
      // Jobs are server-side; the jobId we need came back from upload-ok.
      // upload.jobId lives on the upload payload.
      const confirm = await job.confirmColumn(upload.jobId, columnIndex)
      const header =
        upload.columns.find((c) => c.index === columnIndex)?.header ||
        `Column ${columnIndex + 1}`
      dispatch({
        type: 'column-confirmed',
        columnIndex,
        jobId: upload.jobId,
        confirm,
        headerName: header,
        upload,
      })
    } catch (err) {
      dispatch({
        type: 'column-rejected',
        reason: err instanceof Error ? err.message : 'Column confirm failed',
      })
    }
  }

  async function handlePreviewAccept() {
    if (state.current.phase !== 'column-preview') return
    const { jobId } = state.current
    try {
      const events = await job.loadEvents(jobId)
      dispatch({ type: 'preview-accepted', events })
    } catch {
      // error surfaced; user can retry via reset
    }
  }

  async function handleEventPick(ev: { id: number; name: string }) {
    if (state.current.phase !== 'event-pick') return
    const { jobId } = state.current
    try {
      const plans = await job.loadPlans(jobId, ev.id)
      dispatch({
        type: 'event-picked',
        eventId: ev.id,
        eventName: ev.name,
        plans,
      })
    } catch {
      // error surfaced via job.lastError
    }
  }

  function handlePlanPick(plan: { id: number; name: string }) {
    if (state.current.phase !== 'plan-pick') return
    dispatch({
      type: 'plan-picked',
      planId: plan.id,
      planName: plan.name,
      defaultName: defaultBatchName(state.current.eventName, plan.name),
    })
  }

  async function handleSubmit(body: {
    name: string
    isAutoRedeemed: boolean
    allowOverwriteForDuplicate: boolean
  }) {
    if (state.current.phase !== 'name-it') return
    const { jobId, eventId, planId } = state.current
    dispatch({ type: 'submit-start' })
    try {
      const result = await job.submit(jobId, {
        name: body.name,
        eventId,
        planId,
        isAutoRedeemed: body.isAutoRedeemed,
        allowOverwriteForDuplicate: body.allowOverwriteForDuplicate,
      })
      dispatch({ type: 'submit-ok', result })
    } catch (err) {
      dispatch({
        type: 'submit-failed',
        reason: err instanceof Error ? err.message : 'Submit failed',
      })
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-carnival-50 via-white to-outdoor-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <div className="mx-auto flex max-w-3xl flex-col gap-4 px-4 py-8">
        <header>
          <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">
            Smart Import
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Drop a ticketing export, I'll import the barcodes into Magic Money.
          </p>
        </header>

        {session?.connected ? (
          <MmDisconnectRow
            connectedAt={session.connectedAt}
            onDisconnect={handleDisconnect}
          />
        ) : null}

        {sessionError ? (
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{sessionError}</span>
          </div>
        ) : null}

        <section className="space-y-3">
          {state.scrollback.map((b) => (
            <AgentBubble key={b.id} from={b.from}>
              {b.text}
            </AgentBubble>
          ))}
        </section>

        <section className="flex justify-center">
          <ActiveCard
            phase={state.current}
            busy={job.busy}
            error={job.lastError}
            sessionConnected={session?.connected ?? false}
            onManualConnect={handleManualConnect}
            onFile={handleFile}
            onColumnPick={handleColumnPick}
            onPreviewAccept={handlePreviewAccept}
            onPreviewBack={() => dispatch({ type: 'back-to-upload' })}
            onEventPick={handleEventPick}
            onPlanPick={handlePlanPick}
            onSubmit={handleSubmit}
            onReset={() => dispatch({ type: 'reset' })}
          />
        </section>
      </div>
    </div>
  )
}

interface ActiveCardProps {
  phase: ReturnType<typeof reducer>['current']
  busy: boolean
  error: string | null
  sessionConnected: boolean
  onManualConnect: (auth: string, session: string, alb?: string) => Promise<void>
  onFile: (file: File) => void
  onColumnPick: (idx: number) => void
  onPreviewAccept: () => void
  onPreviewBack: () => void
  onEventPick: (ev: { id: number; name: string }) => void
  onPlanPick: (plan: { id: number; name: string }) => void
  onSubmit: (body: {
    name: string
    isAutoRedeemed: boolean
    allowOverwriteForDuplicate: boolean
  }) => void
  onReset: () => void
}

function ActiveCard({
  phase,
  busy,
  error,
  sessionConnected,
  onManualConnect,
  onFile,
  onColumnPick,
  onPreviewAccept,
  onPreviewBack,
  onEventPick,
  onPlanPick,
  onSubmit,
  onReset,
}: ActiveCardProps) {
  switch (phase.phase) {
    case 'idle':
      if (!sessionConnected) {
        return (
          <MmConnectCard
            busy={busy}
            error={error}
            onManualConnect={onManualConnect}
          />
        )
      }
      return <DropZone onFile={onFile} disabled={busy} />

    case 'need-mm-connection':
      return (
        <MmConnectCard
          busy={busy}
          error={error}
          onManualConnect={onManualConnect}
        />
      )

    case 'uploading':
      return (
        <AgentBubble from="agent">
          Parsing your file…
        </AgentBubble>
      )

    case 'column-confirm':
      return (
        <ColumnConfirmCard
          upload={phase.upload}
          busy={busy}
          onConfirm={onColumnPick}
        />
      )

    case 'column-preview': {
      const header =
        phase.upload.columns.find((c) => c.index === phase.columnIndex)?.header ||
        `Column ${phase.columnIndex + 1}`
      return (
        <PreviewCard
          confirm={phase.confirm}
          columnHeader={header}
          busy={busy}
          onAccept={onPreviewAccept}
          onBack={onPreviewBack}
        />
      )
    }

    case 'event-pick':
      return (
        <EventPickerCard
          events={phase.events}
          busy={busy}
          onPick={onEventPick}
        />
      )

    case 'plan-pick':
      return (
        <PlanPickerCard plans={phase.plans} busy={busy} onPick={onPlanPick} />
      )

    case 'name-it': {
      return (
        <NameAndSubmitCard
          barcodeCount={phase.barcodeCount}
          eventName={phase.eventName}
          planName={phase.planName}
          defaultName={phase.defaultName}
          busy={busy}
          onSubmit={onSubmit}
        />
      )
    }

    case 'submitting':
      return (
        <AgentBubble from="agent">
          Sending the batch to Magic Money…
        </AgentBubble>
      )

    case 'success':
      return <SuccessCard result={phase.result} onReset={onReset} />

    case 'failed':
      return (
        <div className="w-full max-w-xl space-y-3">
          <AgentBubble from="agent">
            Something went sideways: {phase.reason}
          </AgentBubble>
          <button
            type="button"
            onClick={onReset}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            Start over
          </button>
        </div>
      )

    default:
      return null
  }
}
